import React from 'react';
import styled from 'styled-components';
import WidthContainer from '../WidthContainer';

const PrivacyContainer = styled.div`
  padding: 40px 0;
  color: ${({ theme }) => theme.text.regular};
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const PrivacyTitle = styled.h1`
  font-size: 32px;
  margin-bottom: 24px;
  font-weight: 700;
`;

const PrivacyDate = styled.p`
  font-size: 16px;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.text.light};
`;

const PrivacySection = styled.div`
  margin-bottom: 32px;
`;

const PrivacyHeading = styled.h2`
  font-size: 24px;
  margin-bottom: 16px;
  font-weight: 600;
`;

const PrivacySubheading = styled.h3`
  font-size: 20px;
  margin-bottom: 12px;
  font-weight: 500;
`;

const PrivacyParagraph = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const PrivacyList = styled.ul`
  margin-bottom: 16px;
  padding-left: 24px;
`;

const PrivacyListItem = styled.li`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const IndexList = styled.ul`
  margin-bottom: 32px;
  padding-left: 24px;
`;

const IndexItem = styled.li`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.primary.accent};
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PrivacyPolicy = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <WidthContainer>
      <PrivacyContainer>
        <PrivacyTitle>Privacy Policy</PrivacyTitle>
        <PrivacyDate>Effective as of March 17, 2025</PrivacyDate>

        <PrivacySection>
          <PrivacyParagraph>
            We ("DEKSxyz," "we," "us," or "our") operate DEKSxyz, a platform
            that enables users to send, receive, and redeem cryptocurrency gifts
            through a decentralized exchange (DEX) interface. Users can purchase
            digital gift cards, redeem them into supported assets, and interact
            with the blockchain through wallets we provision or that they
            connect themselves.
          </PrivacyParagraph>
          <PrivacyParagraph>
            This Privacy Policy describes how DEKSxyz processes personal
            information that we collect through our website, platform, and
            related services (collectively, the "Service").
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection>
          <PrivacyHeading>Index</PrivacyHeading>
          <IndexList>
            <IndexItem onClick={() => scrollToSection('personal-info')}>
              Personal information we collect
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('tracking')}>
              Tracking technologies
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('use')}>
              How we use your personal information
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('share')}>
              How we share your personal information
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('choices')}>
              Your choices
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('privacy-rights')}>
              Your privacy rights (US residents)
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('retention')}>
              Data retention and deletion
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('security')}>
              Security
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('international')}>
              International data transfers
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('children')}>
              Children's privacy
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('changes')}>
              Changes to this Privacy Policy
            </IndexItem>
            <IndexItem onClick={() => scrollToSection('contact')}>
              How to contact us
            </IndexItem>
          </IndexList>
        </PrivacySection>

        <PrivacySection id='personal-info'>
          <PrivacyHeading>Personal information we collect</PrivacyHeading>

          <PrivacySubheading>Information you provide to us</PrivacySubheading>
          <PrivacyParagraph>
            Personal information you may provide to us through the Service or
            otherwise includes:
          </PrivacyParagraph>

          <PrivacyList>
            <PrivacyListItem>
              Account and profile data, such as your name, email, phone number,
              linked wallet address, and any optional information you provide.
            </PrivacyListItem>
            <PrivacyListItem>
              Communications data, including messages or correspondence when you
              contact us through the Service, email, or social media.
            </PrivacyListItem>
            <PrivacyListItem>
              Transactional data, such as blockchain transaction history, wallet
              interactions, gift card purchases, and redemption details.
            </PrivacyListItem>
            <PrivacyListItem>
              Payment data, which is processed by our payment providers (e.g.,
              Stripe, Coinbase) and not stored by us, but may include
              confirmation of payment, amounts, and timestamps.
            </PrivacyListItem>
            <PrivacyListItem>
              Marketing data, including your preferences for receiving marketing
              communications from us.
            </PrivacyListItem>
            <PrivacyListItem>
              Promotion data, such as information you provide when participating
              in giveaways, contests, or promotional campaigns.
            </PrivacyListItem>
          </PrivacyList>

          <PrivacySubheading>Third-party sources</PrivacySubheading>
          <PrivacyParagraph>
            We may combine personal information received from you with data
            obtained from other sources, including:
          </PrivacyParagraph>

          <PrivacyList>
            <PrivacyListItem>
              Public sources, such as public blockchains and publicly available
              databases.
            </PrivacyListItem>
            <PrivacyListItem>
              Vendors and partners, such as analytics providers, fraud
              prevention services, and blockchain service providers.
            </PrivacyListItem>
            <PrivacyListItem>
              Payment processors, such as Stripe and Coinbase Commerce, which
              provide confirmation data regarding payments you authorize.
            </PrivacyListItem>
          </PrivacyList>

          <PrivacySubheading>Automatic data collection</PrivacySubheading>
          <PrivacyParagraph>
            We and our service providers may automatically collect information
            about you, your device, and your interaction with the Service:
          </PrivacyParagraph>

          <PrivacyList>
            <PrivacyListItem>
              Device data, including IP address, browser type, operating system,
              and unique device identifiers.
            </PrivacyListItem>
            <PrivacyListItem>
              Usage data, such as pages visited, features used, links clicked,
              and time spent.
            </PrivacyListItem>
            <PrivacyListItem>
              Email/communication data, such as open rates and interaction with
              messages.
            </PrivacyListItem>
          </PrivacyList>
        </PrivacySection>

        <PrivacySection id='tracking'>
          <PrivacyHeading>Tracking technologies</PrivacyHeading>
          <PrivacyParagraph>We and our service providers use:</PrivacyParagraph>
          <PrivacyList>
            <PrivacyListItem>
              Cookies, including session and persistent cookies, for
              authentication, analytics, and performance.
            </PrivacyListItem>
            <PrivacyListItem>
              Local storage technologies, such as HTML5, for enhanced user
              experience.
            </PrivacyListItem>
          </PrivacyList>
          <PrivacyParagraph>
            You can manage cookies and related tracking through your browser
            settings.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='use'>
          <PrivacyHeading>How we use your personal information</PrivacyHeading>
          <PrivacyParagraph>
            We may use your personal information to:
          </PrivacyParagraph>

          <PrivacyList>
            <PrivacyListItem>
              Provide, operate, and improve the Service.
            </PrivacyListItem>
            <PrivacyListItem>
              Process transactions, including gift card purchases, wallet
              redemptions, and blockchain interactions.
            </PrivacyListItem>
            <PrivacyListItem>
              Personalize user experience and deliver relevant content.
            </PrivacyListItem>
            <PrivacyListItem>
              Detect, investigate, and prevent fraud, security breaches, or
              illegal activities.
            </PrivacyListItem>
            <PrivacyListItem>
              Comply with legal obligations and enforce our terms.
            </PrivacyListItem>
            <PrivacyListItem>
              Communicate with you, including responding to inquiries, sending
              updates, and delivering promotional messages.
            </PrivacyListItem>
            <PrivacyListItem>
              Conduct analytics to better understand usage and improve features.
            </PrivacyListItem>
          </PrivacyList>
        </PrivacySection>

        <PrivacySection id='share'>
          <PrivacyHeading>
            How we share your personal information
          </PrivacyHeading>
          <PrivacyParagraph>
            We may share your personal information with:
          </PrivacyParagraph>

          <PrivacyList>
            <PrivacyListItem>
              Service providers, such as cloud hosting (AWS), payment processors
              (Stripe, Coinbase), blockchain account management providers (e.g.,
              Privy), and analytics vendors.
            </PrivacyListItem>
            <PrivacyListItem>
              Affiliates and subsidiaries within the DEKSxyz corporate family.
            </PrivacyListItem>
            <PrivacyListItem>
              Professional advisors, such as auditors and lawyers, where
              appropriate.
            </PrivacyListItem>
            <PrivacyListItem>
              Authorities, regulators, or law enforcement, if required by
              applicable law.
            </PrivacyListItem>
            <PrivacyListItem>
              Business transferees, in connection with a merger, acquisition,
              financing, or sale of assets.
            </PrivacyListItem>
            <PrivacyListItem>
              Other users, where interactions are public (e.g., public wallet
              addresses and transactions visible on blockchains).
            </PrivacyListItem>
          </PrivacyList>
          <PrivacyParagraph>
            We do not sell or share personal information for monetary or other
            valuable consideration as defined by US state privacy laws.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='choices'>
          <PrivacyHeading>Your choices</PrivacyHeading>
          <PrivacyList>
            <PrivacyListItem>
              Account settings: You can update your information by contacting us
              or, where available, through in-app settings.
            </PrivacyListItem>
            <PrivacyListItem>
              Marketing communications: You can opt out of marketing emails by
              using the unsubscribe link in those emails.
            </PrivacyListItem>
            <PrivacyListItem>
              Cookies: Manage browser settings to disable or delete cookies.
            </PrivacyListItem>
          </PrivacyList>
        </PrivacySection>

        <PrivacySection id='privacy-rights'>
          <PrivacyHeading>Your privacy rights (US residents)</PrivacyHeading>
          <PrivacyParagraph>
            Residents of certain US states (including California, Virginia,
            Colorado, Connecticut, and Utah) have rights under applicable
            privacy laws. These may include:
          </PrivacyParagraph>
          <PrivacyList>
            <PrivacyListItem>
              Right to know/access the personal information we collect, use, and
              disclose.
            </PrivacyListItem>
            <PrivacyListItem>
              Right to deletion of personal information we hold about you.
            </PrivacyListItem>
            <PrivacyListItem>
              Right to correct inaccurate personal information.
            </PrivacyListItem>
            <PrivacyListItem>
              Right to opt out of the sale or sharing of personal information
              (we do not sell or share).
            </PrivacyListItem>
            <PrivacyListItem>
              Right to limit use of sensitive personal information (we do not
              collect sensitive personal information as defined under applicable
              laws).
            </PrivacyListItem>
            <PrivacyListItem>
              Right to data portability, where applicable.
            </PrivacyListItem>
          </PrivacyList>
          <PrivacyParagraph>
            You may exercise these rights by contacting us at info@deks.xyz. We
            may verify your identity before fulfilling a request.
          </PrivacyParagraph>
          <PrivacyParagraph>
            <strong>Important note on deletion requests:</strong> While we will
            delete personal information that we control (such as email
            addresses, account details, and communications) as required by law,
            certain blockchain records (such as wallet addresses and transaction
            histories) are immutable and cannot be altered or deleted once
            written to the blockchain. In these cases, we will take all steps
            within our control to minimize the use or display of such data, but
            deletion is technically not possible.
          </PrivacyParagraph>
          <PrivacyParagraph>
            We also recognize browser-based Global Privacy Control (GPC) signals
            as opt-out requests where required by law.
          </PrivacyParagraph>
          <PrivacyParagraph>
            We will not discriminate against you for exercising your privacy
            rights.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='retention'>
          <PrivacyHeading>Data retention and deletion</PrivacyHeading>
          <PrivacyParagraph>
            We retain personal information as long as necessary to provide the
            Service, comply with legal obligations, resolve disputes, and
            enforce agreements.
          </PrivacyParagraph>
          <PrivacyParagraph>
            Deletable data (such as account credentials, contact details,
            communications, and off-chain analytics) is removed when no longer
            needed or upon verified deletion request.
          </PrivacyParagraph>
          <PrivacyParagraph>
            Immutable blockchain data (wallet addresses and transaction records
            on public blockchains) cannot be deleted or altered once recorded,
            due to the nature of blockchain technology. We disclose this
            limitation so users can make informed decisions.
          </PrivacyParagraph>
          <PrivacyParagraph>
            When retention is no longer necessary, we securely delete,
            anonymize, or otherwise de-identify data to the extent feasible.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='security'>
          <PrivacyHeading>Security</PrivacyHeading>
          <PrivacyParagraph>
            We implement technical and organizational safeguards to protect
            personal information, including encryption in transit and at rest,
            least-privilege access controls, and continuous monitoring. No
            system can be 100% secure, but we work to protect data against
            unauthorized access, use, alteration, or disclosure.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='international'>
          <PrivacyHeading>International data transfers</PrivacyHeading>
          <PrivacyParagraph>
            We are headquartered in the United States. If you access the Service
            from outside the US, your information may be transferred, stored,
            and processed in the United States or other countries where our
            vendors operate, which may not provide the same data protection laws
            as your home country.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='children'>
          <PrivacyHeading>Children's privacy</PrivacyHeading>
          <PrivacyParagraph>
            The Service is not directed to or intended for individuals under the
            age of 18. We do not knowingly collect personal information from
            minors. If you believe a child has provided us personal information,
            please contact us so we can delete it.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='changes'>
          <PrivacyHeading>Changes to this Privacy Policy</PrivacyHeading>
          <PrivacyParagraph>
            We may update this Privacy Policy from time to time. If material
            changes are made, we will notify you by posting the revised policy
            on the Service and updating the "Effective date" above.
          </PrivacyParagraph>
        </PrivacySection>

        <PrivacySection id='contact'>
          <PrivacyHeading>How to contact us</PrivacyHeading>
          <PrivacyParagraph>
            If you have questions or concerns about this Privacy Policy or our
            privacy practices, you may contact us at:
          </PrivacyParagraph>
          <PrivacyParagraph>Email: info@deks.xyz</PrivacyParagraph>
        </PrivacySection>
      </PrivacyContainer>
    </WidthContainer>
  );
};

export default PrivacyPolicy;
